/**
 * Current User. UserInfo, token, etc.
 */

import { Injectable } from '@angular/core';
import { AuthConfig, tokenNotExpired, JwtHelper } from 'angular2-jwt';
import { CookieService } from 'ngx-cookie';

import { CommonRequestService } from './common-request.service';
import { AppSettings } from './../app.settings';
import { UserSubscription } from './admin.service';
import { Subject } from 'rxjs/Subject';
import { RequestOptions, URLSearchParams } from '@angular/http';

export interface UserInfo {
	customerId: number; // Agency Id
	customerName: string; // Agency Name short
	customerDesc: string; // Agency Name long
	adminEmails: string[]; // Agency Admins' Emails
	userEmail: string;
	userId: number;
	userName: string;
	userProfile: any; // ??
	isAdmin: boolean;
	isSuperAdmin: boolean;
	subscriptionId?: number;
	userSubId?: number;
}
export interface CustomerInfo {
	id: number;
	nameShort: string;
	nameLong: string;
	adminEmails: string[];
	parentId: number;
	parentEmails?: string[];
	level: number;
	givState?: string; // The state the customer belongs to
	children?: CustomerChildInfo[];
}
export interface CustomerChildInfo {
	id: number;
	name: string;
	adminEmails: string[];
	children?: CustomerChildInfo[];
};

@Injectable()
export class UserService {
	// On token updated. Note: subscription changes won't trigger userInfoSubject to update
	public userInfoSubject: Subject<UserInfo> = new Subject();

	private token: string = localStorage.getItem('token');
	private jwtHelper: JwtHelper = new JwtHelper();
	private tokenUpdateInterval;
	private userInfo: UserInfo;
	private hasUserSubscription = false;

	public customerInfo: CustomerInfo;
	// The cookie that will store token after logging in
	// This cookie is for authentication use of applications of different subdomains, e.g HPMS
	// **IMPORTANT** If this is changed, all the subdomain applications which used this cookie should change
	private authCookieName = 'PortalToken';
	private cookieOptions = {
		domain: '.linearbench.com'
	};

	constructor(
		private cookieService: CookieService,
		private requestService: CommonRequestService
	) {

	}

	getUserInfo(): UserInfo {
		return this.userInfo;
	}
	getCurrentToken(): string {
		return this.token;
	}
	setUserInfoFromToken(): void {
		let info: any;
		if (!this.userInfo) {
			this.userInfo = {
				customerId: null,
				customerName: null,
				customerDesc: null,
				adminEmails: [],
				userEmail: null,
				userId: null,
				userName: null,
				userProfile: null,
				isAdmin: null,
				isSuperAdmin: null
			};
		}
		if (!this.token || this.token === 'undefined') {
			console.log('No Token Exists!');
			this.userInfo = {
				customerId: null,
				customerName: null,
				customerDesc: null,
				adminEmails: [],
				userEmail: null,
				userId: null,
				userName: null,
				userProfile: null,
				isAdmin: null,
				isSuperAdmin: null
			};
			return;
		}
		info = this.jwtHelper.decodeToken(this.token);
		// NOTE: Do not create a new userInfo object, some properties such as userSubId might be lost due to this
		this.userInfo.customerId = info.cus_id; // Agency Id
		this.userInfo.customerName = info.cus_name_short; // Agency Name
		this.userInfo.customerDesc = info.cus_name_long;
		this.userInfo.adminEmails = info.admin_emails || []; // Agency Admin's Email
		this.userInfo.userEmail = info.usr_email;
		this.userInfo.userId = info.usr_id;
		this.userInfo.userName = info.usr_name;
		this.userInfo.userProfile = info.usr_profile;
		this.userInfo.isAdmin = info.usr_agency_owner_flag; // info.usr_admin_flag;
		this.userInfo.isSuperAdmin = info.usr_site_admin_flag; // info.usr_sa_flag;
	}
	updateUserInfoFromUserSub(userSub: UserSubscription) {
		this.userInfo.subscriptionId = userSub.subId;
		this.userInfo.userSubId = userSub.userSubId;
	}
	updateToken() {
		console.log('Service Called: renew. Token updated.');
		const url = AppSettings.serviceRootUrl + 'admin/login';
		return new Promise((resolve, reject) => {
			this.requestService.requestGet(url, null, (rsp) => {
				this.setUpdatedToken(rsp.data.content);
				resolve();
			}, err => reject(err));
		});
	}
	setUpdatedToken(token) {
		if (!token) {
			console.log('token updated failed. token: ' + token);
			return;
		}
		this.token = token;
		localStorage.setItem('token', token);
		this.setUserInfoFromToken();
		// Update cookie
		this.cookieService.put(this.authCookieName, token, this.cookieOptions);
		//
		this.userInfoSubject.next(this.userInfo);
	}
	// Set App to update token automatically every XX second
	setTokenUpdateInterval() {
		console.log('set token update interval');
		if (this.tokenUpdateInterval) {
			clearInterval(this.tokenUpdateInterval);
		}
		this.tokenUpdateInterval = setInterval(() => {
			this.updateToken();
		}, 560000);
	}
	clearTokenUpdateInterval() {
		console.log('clear token update interval. Warning: Token Stop Updating!!!');
		if (this.tokenUpdateInterval) {
			clearInterval(this.tokenUpdateInterval);
			this.tokenUpdateInterval = null;
		}
	}

	removeToken() {
		// clear token remove user from local storage to log user out
		this.token = null;
		localStorage.removeItem('token');
		localStorage.removeItem('userInfo');
		this.cookieService.remove(this.authCookieName, this.cookieOptions);
	}

	getHasUserSubscription() {
		return this.hasUserSubscription;
	}
	setHasUserSubscription(hasUserSub: boolean) {
		this.hasUserSubscription = hasUserSub;
	}
	isTokenExpired() {
		return !tokenNotExpired();
	}
	isLoggedIn() {
		console.log(tokenNotExpired());
		return tokenNotExpired() && this.hasUserSubscription;
	}

	getCustomerInfo(cusId?, skipUpdateOrigin?) {
		return new Promise<CustomerInfo>((resolve, reject) => {
			const customerId = cusId || this.getUserInfo().customerId;
			const params: URLSearchParams = new URLSearchParams();
			params.set('customerId', JSON.stringify(customerId));
			const options = new RequestOptions();
			options.params = params;
			const url = 'admin/customer';
			this.requestService.requestGet(AppSettings.serviceRootUrl + url, options, (rsp) => {
				if (rsp.data) {
					const data = rsp.data[0];
					const info: CustomerInfo = {
						id: data.cus_id,
						nameLong: data.cus_name_long,
						nameShort: data.cus_name_short,
						adminEmails: data.admin_emails,
						level: data.level,
						parentId: data.cus_parent_id,
						children: data.cus_child ? data.cus_child.map(c => {
							return {
								id: c.id,
								name: c.name,
								adminEmails: c.admin_emails
							};
						}) : []
					};
					// sort children
					info.children.sort(this.sortCusByName);
					if (data.cus_state) { // Do not set givState if not exist
						info.givState = data.cus_state;
					}
					if (!skipUpdateOrigin) {
						this.customerInfo = info;
					}
					resolve(info);
				} else {
					console.log('get customer service return null');
					resolve();
				}
			}, err => reject(err));
		});
	}

	sortCusByName(a, b) {
		if (a.name > b.name) {
			return 1;
		} else if (a.name < b.name) {
			return -1;
		} else {
			return 0;
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/common-services/user.service.ts